import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@material-ui/core';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import React from 'react';
import type { OfflineTerminalDialogState } from 'helpers/offlineTerminal/offlineTerminalTypes';

type Props = {
	state: OfflineTerminalDialogState;
	errorMessage?: string;
	waitingTitle?: string;
	waitingMessage?: string;
	onContinue?: () => void;
	continueLabel?: string;
	onRetry?: () => void;
	onInquiry?: () => void;
	onCancel?: () => void;
};

/**
 * Shows while waiting for customer on the payment terminal,
 * or when payment failed (Retry / Transaction Inquiry / Cancel).
 */
export const OfflineTerminalDialog: React.FC<Props> = ({
	state,
	errorMessage,
	waitingTitle = 'Waiting for Terminal',
	waitingMessage = 'Please complete payment on the terminal (tap, insert, or swipe card).',
	onContinue,
	continueLabel = 'Continue — Charge New Card',
	onRetry,
	onInquiry,
	onCancel,
}) => {
	if (state === 'idle') return null;

	const isWaiting = state === 'waiting';
	const isVoidComplete = state === 'voidComplete';
	const isFailed = state === 'failed';

	return (
		<Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown>
			<DialogTitle>
				{isWaiting
					? waitingTitle
					: isVoidComplete
					? waitingTitle
					: 'Payment Failed'}
			</DialogTitle>
			<DialogContent>
				{isWaiting && (
					<>
						<Loading />
						<Typography style={{ marginTop: 16 }}>{waitingMessage}</Typography>
						<Typography variant="body2" color="textSecondary">
							Do not close this window.
						</Typography>
					</>
				)}
				{isVoidComplete && (
					<>
						<Typography style={{ marginTop: 8 }}>{waitingMessage}</Typography>
						<Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
							When you are ready, continue below to charge the new card (one tap on
							the terminal).
						</Typography>
					</>
				)}
				{isFailed && (
					<>
						<Typography color="error" gutterBottom>
							PAYMENT FAILED
						</Typography>
						<Typography variant="body2">
							{errorMessage ||
								'The terminal did not approve this payment. You can retry, check transaction status, or cancel.'}
						</Typography>
					</>
				)}
			</DialogContent>
			{isVoidComplete && (
				<DialogActions>
					<Button onClick={onCancel} color="default">
						Cancel
					</Button>
					<Button onClick={onContinue} color="primary" variant="contained">
						{continueLabel}
					</Button>
				</DialogActions>
			)}
			{isFailed && (
				<DialogActions>
					<Button onClick={onCancel} color="default">
						Cancel
					</Button>
					<Button onClick={onInquiry} color="primary">
						Transaction Inquiry
					</Button>
					<Button onClick={onRetry} color="primary" variant="contained">
						Retry
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
};
